// User Account Management Service
// Handles user registration, authentication, and account creation

export interface UserAccount {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  lastLogin: string;
  isActive: boolean;
}

export interface CreateAccountRequest {
  email: string;
  name: string;
  password: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AccountResponse {
  success: boolean;
  message: string;
  user?: UserAccount;
  token?: string;
}

// Environment configuration
const ACCOUNT_CONFIG = {
  endpoint: import.meta.env.VITE_API_URL || import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3001',
  enabled: import.meta.env.VITE_ENABLE_ACCOUNTS !== 'false', // Default to enabled
  timeout: 10000, // 10 second timeout
  retryAttempts: 3,
  retryDelay: 1000 // 1 second between retries
};

// HTTP request with retry logic for account operations
async function makeAccountRequest(url: string, data: Record<string, unknown>, attempt: number = 1): Promise<AccountResponse> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ACCOUNT_CONFIG.timeout);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Version': '1.0.0',
        'X-Operation': 'account-management'
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      if (response.status === 409) {
        // Account already exists – return structured response rather than throwing so UI can surface friendly message
        return {
          success: false,
          message: 'An account with this e-mail already exists. Please log in instead.'
        } as AccountResponse;
      }
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error(`Account request attempt ${attempt} failed:`, error);
    
    // Retry logic
    if (attempt < ACCOUNT_CONFIG.retryAttempts) {
      console.log(`Retrying account operation in ${ACCOUNT_CONFIG.retryDelay}ms... (attempt ${attempt + 1}/${ACCOUNT_CONFIG.retryAttempts})`);
      await new Promise(resolve => setTimeout(resolve, ACCOUNT_CONFIG.retryDelay));
      return makeAccountRequest(url, data, attempt + 1);
    }
    
    // Final failure
    throw new Error(`Failed to complete account operation after ${ACCOUNT_CONFIG.retryAttempts} attempts: ${error}`);
  }
}

// Password validation
export function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

// Email validation
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Main account service
export const accountService = {
  
  // Create new user account
  async createAccount(request: CreateAccountRequest): Promise<AccountResponse> {
    
    // Check if account creation is enabled
    if (!ACCOUNT_CONFIG.enabled) {
      console.log('Account creation disabled via environment variable');
      return {
        success: false,
        message: 'Account creation is currently disabled'
      };
    }


    
    try {
      // Validate email
      if (!validateEmail(request.email)) {
        return {
          success: false,
          message: 'Please enter a valid email address'
        };
      }
      
      // Validate password
      const passwordValidation = validatePassword(request.password);
      if (!passwordValidation.isValid) {
        return {
          success: false,
          message: passwordValidation.errors.join(', ')
        };
      }
      
      console.log('Creating user account:', {
        email: request.email,
        name: request.name.substring(0, 10) + '...', // Partial for privacy
        passwordLength: request.password.length
      });
      
      const response = await makeAccountRequest(`${ACCOUNT_CONFIG.endpoint}/api/accounts/create`, request);
      
      if (response.success && response.user) {
        console.log('✅ User account created successfully');
        
        // Store account token locally (encrypted)
        if (response.token) {
          try {
            localStorage.setItem('account-token', response.token);
            console.log('Account token stored locally');
          } catch (error) {
            console.warn('Failed to store account token:', error);
          }
        }
        
        return response;
      } else {
        console.warn('⚠️ Account creation failed:', response.message);
        return response;
      }
      
    } catch (error) {
      console.error('❌ Failed to create user account:', error);
      
      // Store failed account creation for retry later
      try {
        const failedAccounts = JSON.parse(localStorage.getItem('failed-accounts') || '[]');
        failedAccounts.push({
          email: request.email,
          name: request.name,
          timestamp: new Date().toISOString(),
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        localStorage.setItem('failed-accounts', JSON.stringify(failedAccounts));
        console.log('📝 Failed account creation stored for retry');
      } catch (storageError) {
        console.error('Failed to store failed account creation:', storageError);
      }
      
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create account'
      };
    }
  },
  
  // Login existing user
  async login(request: LoginRequest): Promise<AccountResponse> {
    
    if (!ACCOUNT_CONFIG.enabled) {
      return {
        success: false,
        message: 'Account login is currently disabled'
      };
    }


    
    try {
      if (!validateEmail(request.email)) {
        return {
          success: false,
          message: 'Please enter a valid email address'
        };
      }
      
      console.log('Logging in user:', {
        email: request.email.substring(0, 10) + '...' // Partial for privacy
      });
      
      const response = await makeAccountRequest(`${ACCOUNT_CONFIG.endpoint}/api/accounts/login`, request);
      
      if (response.success && response.user && response.token) {
        console.log('✅ User login successful');
        
        // Store account token locally
        try {
          localStorage.setItem('account-token', response.token);
          console.log('Account token stored locally');
        } catch (error) {
          console.warn('Failed to store account token:', error);
        }
        
        return response;
      } else {
        console.warn('⚠️ Login failed:', response.message);
        return response;
      }
      
    } catch (error) {
      console.error('❌ Failed to login user:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to login'
      };
    }
  },
  
  // Get stored account token
  getAccountToken(): string | null {
    try {
      return localStorage.getItem('account-token');
    } catch (error) {
      console.warn('Failed to retrieve account token:', error);
      return null;
    }
  },
  
  // Clear stored account token (logout)
  logout(): void {
    try {
      localStorage.removeItem('account-token');
      console.log('Account token cleared');
    } catch (error) {
      console.warn('Failed to clear account token:', error);
    }
  },
  
  // Check if user is logged in
  isLoggedIn(): boolean {
    return this.getAccountToken() !== null;
  },
  
  // Retry failed account creations
  async retryFailedAccounts(): Promise<number> {
    if (!ACCOUNT_CONFIG.enabled) {
      return 0;
    }
    
    try {
      const failedAccounts = JSON.parse(localStorage.getItem('failed-accounts') || '[]');
      
      if (failedAccounts.length === 0) {
        return 0;
      }
      
      console.log(`🔄 Retrying ${failedAccounts.length} failed account creations...`);
      
      let successCount = 0;
      const stillFailed = [];
      
      for (const failed of failedAccounts) {
        try {
          const result = await this.createAccount({
            email: failed.email,
            name: failed.name,
            password: 'retry-password' // This would need to be handled differently in production
          });
          
          if (result.success) {
            successCount++;
          } else {
            stillFailed.push(failed);
          }
        } catch (error) {
          console.error('Failed to retry account creation:', error);
          stillFailed.push(failed);
        }
      }
      
      // Update failed accounts list
      localStorage.setItem('failed-accounts', JSON.stringify(stillFailed));
      
      console.log(`✅ Retried ${successCount} account creations successfully`);
      return successCount;
      
    } catch (error) {
      console.error('Failed to retry account creations:', error);
      return 0;
    }
  },
  
  // Test account service connection
  async testConnection(): Promise<boolean> {
    if (!ACCOUNT_CONFIG.enabled) {
      return false;
    }
    
    try {
      const response = await fetch(`${ACCOUNT_CONFIG.endpoint}/api/accounts/health`, {
        method: 'GET',
        headers: {
          'X-Client-Version': '1.0.0'
        }
      });
      
      return response.ok;
    } catch (error) {
      console.error('Account service connection test failed:', error);
      return false;
    }
  }
};